import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  IPersonalizationState,
  IPersonalizationSettings,
  IPersonalWidget,
  IPersonalKPI,
  IPersonalFilter,
  IDashboardLayout,
  IWidgetTemplate,
  IPersonalizationExport,
  CreatePerformanceMetricsDto,
  GetPerformanceMetricsRequest,
  CalculatePerformanceMetricsRequest,
  TemporalPatternAnalysisDto,
  CreateTemporalPatternDto,
  AdaptPreferencesRequest,
} from '../types/personalization';
import {
  getPerformanceMetrics,
  createPerformanceMetrics as createPerformanceMetricsApi,
  calculatePerformanceMetrics,
  getPerformanceMetricsStatistics,
  analyzeTemporalPatterns,
  createTemporalPattern as createTemporalPatternApi,
  getActiveTemporalPatterns,
  adaptPreferences,
  getTemporalPatternRecommendations,
} from '../services/api/personalization.api';
import { logger } from '../utils/logger';

// Начальное состояние
const initialState: IPersonalizationState = {
  settings: null,
  widgets: [],
  kpis: [],
  filters: [],
  layouts: [],
  templates: [],
  
  // Метрики производительности
  performanceMetrics: [],
  performanceMetricsStatistics: null,
  
  // Временные паттерны
  temporalPatterns: [],
  activeTemporalPatterns: [],
  temporalPatternRecommendations: null,
  
  isEditMode: false,
  selectedWidget: null,
  isSettingsOpen: false,
  isTemplateLibraryOpen: false,
  
  loading: {
    settings: false,
    widgets: false,
    templates: false,
    performanceMetrics: false,
    temporalPatterns: false,
  },
  
  error: null,
};

// Async thunks для API вызовов
export const loadPersonalizationSettings = createAsyncThunk(
  'personalization/loadSettings',
  async (userId: string) => {
    // Пока используем localStorage, позже подключим API
    const saved = localStorage.getItem(`personalization_${userId}`);
    if (saved) {
      return JSON.parse(saved);
    }
    
    // Дефолтные настройки
    const defaultSettings: IPersonalizationSettings = {
      id: `settings_${userId}`,
      userId,
      autoRefreshInterval: 30,
      gridSize: {
        cols: 12,
        rowHeight: 150,
      },
      favoriteWidgets: [],
      quickFilters: [],
      preferences: {
        showWelcomeMessage: true,
        showHelperTips: true,
        enableAnimations: true,
        enableSounds: false,
        theme: 'light' as const,
        defaultView: 'grid' as const,
        itemsPerPage: 20,
        sortBy: 'name',
        sortOrder: 'asc' as const
      },
      alerts: {
        enableKPIAlerts: true,
        emailNotifications: true,
        pushNotifications: false,
        alertFrequency: 'immediate',
      },
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    return defaultSettings;
  }
);

export const savePersonalizationSettings = createAsyncThunk(
  'personalization/saveSettings',
  async (settings: IPersonalizationSettings) => {
    // Сохраняем в localStorage
    localStorage.setItem(`personalization_${settings.userId}`, JSON.stringify(settings));
    return settings;
  }
);

export const loadWidgetTemplates = createAsyncThunk(
  'personalization/loadTemplates',
  async () => {
    // Дефолтные шаблоны виджетов
    const templates: IWidgetTemplate[] = [
      {
        id: 'kpi-basic',
        name: 'Базовый KPI',
        description: 'Простое отображение ключевого показателя',
        type: 'kpi',
        category: 'metrics',
        icon: 'trending_up',
        defaultSize: 'small',
        configSchema: {
          title: { type: 'string', required: true },
          value: { type: 'number', required: true },
          target: { type: 'number' },
          color: { type: 'string' },
        },
        isBuiltIn: true,
        isPublic: true,
        usageCount: 0,
        rating: 5,
        tags: ['kpi', 'metrics', 'basic'],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'chart-line-basic',
        name: 'Линейный график',
        description: 'График тренда по времени',
        type: 'chart-line',
        category: 'charts',
        icon: 'show_chart',
        defaultSize: 'medium',
        configSchema: {
          title: { type: 'string', required: true },
          dataSource: { type: 'string', required: true },
          xAxis: { type: 'string' },
          yAxis: { type: 'string' },
        },
        isBuiltIn: true,
        isPublic: true,
        usageCount: 0,
        rating: 4.8,
        tags: ['chart', 'line', 'trend'],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
      {
        id: 'progress-basic',
        name: 'Индикатор прогресса',
        description: 'Отображение прогресса выполнения',
        type: 'progress',
        category: 'indicators',
        icon: 'donut_large',
        defaultSize: 'small',
        configSchema: {
          title: { type: 'string', required: true },
          value: { type: 'number', required: true },
          max: { type: 'number', required: true },
          color: { type: 'string' },
        },
        isBuiltIn: true,
        isPublic: true,
        usageCount: 0,
        rating: 4.5,
        tags: ['progress', 'indicator', 'percent'],
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];
    
    return templates;
  }
);

// ===== МЕТРИКИ ПРОИЗВОДИТЕЛЬНОСТИ =====

export const getPerformanceMetricsThunk = createAsyncThunk(
  'personalization/getPerformanceMetrics',
  async (params?: GetPerformanceMetricsRequest) => {
    try {
      const metrics = await getPerformanceMetrics(params);
      return metrics;
    } catch (error) {
      logger.error('Ошибка получения метрик производительности:', error);
      throw error;
    }
  }
);

export const createPerformanceMetricsThunk = createAsyncThunk(
  'personalization/createPerformanceMetrics',
  async (payload: CreatePerformanceMetricsDto) => {
    try {
      const metric = await createPerformanceMetricsApi(payload);
      return metric;
    } catch (error) {
      logger.error('Ошибка создания метрики производительности:', error);
      throw error;
    }
  }
);

export const calculatePerformanceMetricsThunk = createAsyncThunk(
  'personalization/calculatePerformanceMetrics',
  async (params?: CalculatePerformanceMetricsRequest) => {
    try {
      const metric = await calculatePerformanceMetrics(params);
      return metric;
    } catch (error) {
      logger.error('Ошибка расчета метрик производительности:', error);
      throw error;
    }
  }
);

export const getPerformanceMetricsStatisticsThunk = createAsyncThunk(
  'personalization/getPerformanceMetricsStatistics',
  async () => {
    try {
      const statistics = await getPerformanceMetricsStatistics();
      return statistics;
    } catch (error) {
      logger.error('Ошибка получения статистики метрик производительности:', error);
      throw error;
    }
  }
);

// ===== ВРЕМЕННЫЕ ПАТТЕРНЫ =====

export const analyzeTemporalPatternsThunk = createAsyncThunk(
  'personalization/analyzeTemporalPatterns',
  async (payload: TemporalPatternAnalysisDto) => {
    try {
      const patterns = await analyzeTemporalPatterns(payload);
      return patterns;
    } catch (error) {
      logger.error('Ошибка анализа временных паттернов:', error);
      throw error;
    }
  }
);

export const createTemporalPatternThunk = createAsyncThunk(
  'personalization/createTemporalPattern',
  async (payload: CreateTemporalPatternDto) => {
    try {
      const pattern = await createTemporalPatternApi(payload);
      return pattern;
    } catch (error) {
      logger.error('Ошибка создания временного паттерна:', error);
      throw error;
    }
  }
);

export const getActiveTemporalPatternsThunk = createAsyncThunk(
  'personalization/getActiveTemporalPatterns',
  async () => {
    try {
      const patterns = await getActiveTemporalPatterns();
      return patterns;
    } catch (error) {
      logger.error('Ошибка получения активных временных паттернов:', error);
      throw error;
    }
  }
);

export const adaptPreferencesThunk = createAsyncThunk(
  'personalization/adaptPreferences',
  async (payload: AdaptPreferencesRequest, { dispatch, getState }) => {
    try {
      await adaptPreferences(payload);
      // После адаптации настроек, загружаем обновленные настройки
      const state = getState() as { personalization: IPersonalizationState };
      if (state.personalization.settings?.userId) {
        await dispatch(loadPersonalizationSettings(state.personalization.settings.userId));
      }
    } catch (error) {
      logger.error('Ошибка адаптации настроек:', error);
      throw error;
    }
  }
);

export const getTemporalPatternRecommendationsThunk = createAsyncThunk(
  'personalization/getTemporalPatternRecommendations',
  async () => {
    try {
      const recommendations = await getTemporalPatternRecommendations();
      return recommendations;
    } catch (error) {
      logger.error('Ошибка получения рекомендаций временных паттернов:', error);
      throw error;
    }
  }
);

export const exportPersonalization = createAsyncThunk(
  'personalization/export',
  async (_, { getState }) => {
    const state = getState() as { personalization: IPersonalizationState };
    const { settings, widgets, kpis, filters, layouts } = state.personalization;
    
    if (!settings) throw new Error('Настройки не загружены');
    
    const exportData: IPersonalizationExport = {
      version: '1.0',
      exportDate: new Date(),
      userId: settings.userId,
      settings,
      widgets,
      kpis,
      filters,
      layouts,
    };
    
    return exportData;
  }
);

// Создаем slice
const personalizationSlice = createSlice({
  name: 'personalization',
  initialState,
  reducers: {
    // Управление виджетами
    addWidget: (state, action: PayloadAction<IPersonalWidget>) => {
      state.widgets.push(action.payload);
    },
    
    updateWidget: (state, action: PayloadAction<Partial<IPersonalWidget> & { id: string }>) => {
      const index = state.widgets.findIndex(w => w.id === action.payload.id);
      if (index !== -1) {
        const existing = state.widgets[index];
        if (!existing) {
          return;
        }
        state.widgets[index] = {
          ...existing,
          ...action.payload,
          type: existing.type,
          title: existing.title,
          config: existing.config,
          updatedAt: new Date(),
        };
      }
    },
    
    removeWidget: (state, action: PayloadAction<string>) => {
      state.widgets = state.widgets.filter(w => w.id !== action.payload);
    },
    
    toggleWidgetFavorite: (state, action: PayloadAction<string>) => {
      const widget = state.widgets.find(w => w.id === action.payload);
      if (widget) {
        widget.isFavorite = !widget.isFavorite;
        widget.updatedAt = new Date();
        
        // Обновляем список избранных в настройках
        if (state.settings) {
          if (widget.isFavorite) {
            state.settings.favoriteWidgets.push(widget.id);
          } else {
            state.settings.favoriteWidgets = state.settings.favoriteWidgets.filter(id => id !== widget.id);
          }
        }
      }
    },
    
    // Управление KPI
    addKPI: (state, action: PayloadAction<IPersonalKPI>) => {
      state.kpis.push(action.payload);
    },
    
    updateKPI: (state, action: PayloadAction<Partial<IPersonalKPI> & { id: string }>) => {
      const index = state.kpis.findIndex(k => k.id === action.payload.id);
      if (index !== -1) {
        const existing = state.kpis[index];
        if (!existing) {
          return;
        }
        state.kpis[index] = {
          ...existing,
          ...action.payload,
          name: existing.name,
          color: existing.color,
          category: existing.category,
          source: existing.source,
          updatedAt: new Date(),
        };
      }
    },
    
    removeKPI: (state, action: PayloadAction<string>) => {
      state.kpis = state.kpis.filter(k => k.id !== action.payload);
    },
    
    reorderKPIs: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      const [removed] = state.kpis.splice(fromIndex, 1);
      if (!removed) {
        return;
      }
      state.kpis.splice(toIndex, 0, removed);
      
      // Обновляем порядок
      state.kpis.forEach((kpi, index) => {
        kpi.order = index;
        kpi.updatedAt = new Date();
      });
    },
    
    // Управление фильтрами
    addFilter: (state, action: PayloadAction<IPersonalFilter>) => {
      state.filters.push(action.payload);
    },
    
    updateFilter: (state, action: PayloadAction<Partial<IPersonalFilter> & { id: string }>) => {
      const index = state.filters.findIndex(f => f.id === action.payload.id);
      if (index !== -1) {
        const existing = state.filters[index];
        if (!existing) {
          return;
        }
        state.filters[index] = {
          ...existing,
          ...action.payload,
          name: existing.name,
          filterConfig: existing.filterConfig,
          updatedAt: new Date(),
        };
      }
    },
    
    removeFilter: (state, action: PayloadAction<string>) => {
      state.filters = state.filters.filter(f => f.id !== action.payload);
    },
    
    setDefaultFilter: (state, action: PayloadAction<string>) => {
      // Убираем default с других фильтров
      state.filters.forEach(f => {
        f.isDefault = f.id === action.payload;
        f.updatedAt = new Date();
      });
    },
    
    // Управление лайаутами
    addLayout: (state, action: PayloadAction<IDashboardLayout>) => {
      state.layouts.push(action.payload);
    },
    
    updateLayout: (state, action: PayloadAction<Partial<IDashboardLayout> & { id: string }>) => {
      const index = state.layouts.findIndex(l => l.id === action.payload.id);
      if (index !== -1) {
        const existing = state.layouts[index];
        if (!existing) {
          return;
        }
        state.layouts[index] = {
          ...existing,
          ...action.payload,
          name: existing.name,
          widgets: existing.widgets,
          updatedAt: new Date(),
        };
      }
    },
    
    removeLayout: (state, action: PayloadAction<string>) => {
      state.layouts = state.layouts.filter(l => l.id !== action.payload);
    },
    
    // UI управление
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditMode = action.payload;
    },
    
    setSelectedWidget: (state, action: PayloadAction<string | null>) => {
      state.selectedWidget = action.payload;
    },
    
    setSettingsOpen: (state, action: PayloadAction<boolean>) => {
      state.isSettingsOpen = action.payload;
    },
    
    setTemplateLibraryOpen: (state, action: PayloadAction<boolean>) => {
      state.isTemplateLibraryOpen = action.payload;
    },
    
    // Обновление настроек
    updateSettings: (state, action: PayloadAction<Partial<IPersonalizationSettings>>) => {
      if (state.settings) {
        state.settings = { ...state.settings, ...action.payload, updatedAt: new Date() };
      }
    },
    
    // Очистка ошибок
    clearError: (state) => {
      state.error = null;
    },
  },
  
  extraReducers: (builder) => {
    // Загрузка настроек
    builder.addCase(loadPersonalizationSettings.pending, (state) => {
      state.loading.settings = true;
      state.error = null;
    });
    
    builder.addCase(loadPersonalizationSettings.fulfilled, (state, action) => {
      state.loading.settings = false;
      state.settings = action.payload;
    });
    
    builder.addCase(loadPersonalizationSettings.rejected, (state, action) => {
      state.loading.settings = false;
      state.error = action.error.message || 'Ошибка загрузки настроек';
    });
    
    // Сохранение настроек
    builder.addCase(savePersonalizationSettings.fulfilled, (state, action) => {
      state.settings = action.payload;
    });
    
    // Загрузка шаблонов
    builder.addCase(loadWidgetTemplates.pending, (state) => {
      state.loading.templates = true;
    });
    
    builder.addCase(loadWidgetTemplates.fulfilled, (state, action) => {
      state.loading.templates = false;
      state.templates = action.payload;
    });
    
    // ===== МЕТРИКИ ПРОИЗВОДИТЕЛЬНОСТИ =====
    
    builder.addCase(getPerformanceMetricsThunk.pending, (state) => {
      state.loading.performanceMetrics = true;
      state.error = null;
    });
    
    builder.addCase(getPerformanceMetricsThunk.fulfilled, (state, action) => {
      state.loading.performanceMetrics = false;
      state.performanceMetrics = action.payload;
    });
    
    builder.addCase(getPerformanceMetricsThunk.rejected, (state, action) => {
      state.loading.performanceMetrics = false;
      state.error = action.error.message || 'Ошибка получения метрик производительности';
    });
    
    builder.addCase(createPerformanceMetricsThunk.fulfilled, (state, action) => {
      state.performanceMetrics.push(action.payload);
    });
    
    builder.addCase(calculatePerformanceMetricsThunk.fulfilled, (state, action) => {
      state.performanceMetrics.push(action.payload);
    });
    
    builder.addCase(getPerformanceMetricsStatisticsThunk.fulfilled, (state, action) => {
      state.performanceMetricsStatistics = action.payload;
    });
    
    // ===== ВРЕМЕННЫЕ ПАТТЕРНЫ =====
    
    builder.addCase(analyzeTemporalPatternsThunk.pending, (state) => {
      state.loading.temporalPatterns = true;
      state.error = null;
    });
    
    builder.addCase(analyzeTemporalPatternsThunk.fulfilled, (state, action) => {
      state.loading.temporalPatterns = false;
      state.temporalPatterns = action.payload;
    });
    
    builder.addCase(analyzeTemporalPatternsThunk.rejected, (state, action) => {
      state.loading.temporalPatterns = false;
      state.error = action.error.message || 'Ошибка анализа временных паттернов';
    });
    
    builder.addCase(createTemporalPatternThunk.fulfilled, (state, action) => {
      state.temporalPatterns.push(action.payload);
      if (action.payload.isActive) {
        state.activeTemporalPatterns.push(action.payload);
      }
    });
    
    builder.addCase(getActiveTemporalPatternsThunk.fulfilled, (state, action) => {
      state.activeTemporalPatterns = action.payload;
    });
    
    builder.addCase(getTemporalPatternRecommendationsThunk.fulfilled, (state, action) => {
      state.temporalPatternRecommendations = action.payload;
    });
    
    builder.addCase(loadWidgetTemplates.rejected, (state, action) => {
      state.loading.templates = false;
      state.error = action.error.message || 'Ошибка загрузки шаблонов';
    });
  },
});

export const {
  addWidget,
  updateWidget,
  removeWidget,
  toggleWidgetFavorite,
  addKPI,
  updateKPI,
  removeKPI,
  reorderKPIs,
  addFilter,
  updateFilter,
  removeFilter,
  setDefaultFilter,
  addLayout,
  updateLayout,
  removeLayout,
  setEditMode,
  setSelectedWidget,
  setSettingsOpen,
  setTemplateLibraryOpen,
  updateSettings,
  clearError,
} = personalizationSlice.actions;

export default personalizationSlice.reducer; 